import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, OnDestroy, signal, ViewChild } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { filter, firstValueFrom, Subject, takeUntil } from "rxjs";
import {
  slideAnimation,
  SlideAnimationPageKey,
  SlideAnimationTrigger,
} from "src/app/animations/slide-animation";
import { BackButtonComponent } from "src/app/back-button/back-button.component";
import Constants from "src/app/constants/constants";
import LocalizationConstants from "src/app/constants/localization-constants";
import { CompanyService } from "src/app/services/company.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { TechCoverContactService } from "src/app/services/tech-cover-contact.service";
import { TechCoverSoftwareReleaseService } from "src/app/services/tech-cover-software-release.service";
import { TechCoverTicketsDataService } from "src/app/services/tech-cover-tickets-data.service";
import { getFamFromCompany } from "src/app/utils/company.utils";
import { materialCommonImports } from "src/app/utils/import.utils";

@Component({
  selector: "app-tech-cover",
  templateUrl: "./tech-cover.component.html",
  styleUrls: ["./tech-cover.component.scss"],
  standalone: true,
  imports: [
    RouterModule,
    TranslateModule,
    BackButtonComponent,
    CommonModule,
    ...materialCommonImports,
  ],
  animations: [slideAnimation],
})
export class TechCoverComponent implements AfterViewInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  @ViewChild(RouterOutlet) outlet: RouterOutlet;
  localizationConstants = LocalizationConstants;
  animationKey: string | null = this.getSlideAnimationKey();

  techCoverContact = signal(null);

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private techCoverContactService: TechCoverContactService,
    private techCoverSoftwareReleaseService: TechCoverSoftwareReleaseService,
    private overlayService: GlobalOverlayService,
    private companyService: CompanyService,
    public techCoverTicketsDataService: TechCoverTicketsDataService,
  ) {
    this.loadData();
    this.companyService.companyChange
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => this.loadData());
  }

  ngAfterViewInit() {
    this.subscribeToRouteEvents();
  }

  private subscribeToRouteEvents(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.animationKey = this.getSlideAnimationKey();
    });
  }

  // Set animation key if the current route has the slide animation trigger
  private getSlideAnimationKey(): string {
    const currentRoute = this.activatedRoute.firstChild;
    return currentRoute?.snapshot?.data[Constants.ROUTE_DATA_ANIMATION_PROP] ===
      SlideAnimationTrigger
      ? SlideAnimationPageKey
      : null;
  }

  private async loadData() {
    this.overlayService.show();

    const contactPromise = firstValueFrom(
      this.techCoverContactService.getContact(
        getFamFromCompany(this.companyService.selectedCompany.name),
      ),
    );

    const loadTicketsAndSoftware = this.techCoverTicketsDataService.loadDataAsync();

    const [, contact] = await Promise.all([loadTicketsAndSoftware, contactPromise]);

    this.techCoverContact.set(contact);

    this.overlayService.close();
  }

  public async onSoftwareDownloadClick(softwareMetadataId: number): Promise<void> {
    const url = await this.techCoverSoftwareReleaseService.getSoftwareDownloadUrl(
      this.companyService.selectedCompany.name,
      softwareMetadataId,
    );
    window.open(url);
  }

  public async onReleaseNoteDownloadClick(softwareMetadataId: number): Promise<void> {
    const url = await this.techCoverSoftwareReleaseService.getReleaseNoteUrl(
      this.companyService.selectedCompany.name,
      softwareMetadataId,
    );
    window.open(url);
  }

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
